<template lang="html">
<or-modal
    ref="modal"
    dismiss-on="close-button"
    class="prebuilt-model-import--modal"
    :title="importModelTitle"
    :dismissible="!isRemoving"
    @close="handleImportModelClose"
    @open="onOpen"
>
    <div class="prebuilt-model-import--body">
        <div
          v-if="!isAdded"
          class="import-model--help-text"
        >
            {{ $t('model.nlu-model.prebuilt-models.prebuilt-model-card.chooseTheContext') }}
        </div>
        <or-switch
            v-if="!isAdded"
            v-model="importModal.createNewContext"
            class="import-model--context-type"
            :disabled="isRemoving"
        >
            {{ $t('model.nlu-model.prebuilt-models.prebuilt-model-card.importToThe') }}
        </or-switch>

        <or-progress-circular
          v-if="isLoadingList"
          color="primary"
          class="spiner"
        ></or-progress-circular>
        <div v-show="!isLoadingList">
            <or-textbox
                v-if="importModal.createNewContext && !isAdded"
                v-model="$v.importModal.$model.newContextName"
                :placeholder="$t('model.nlu-model.prebuilt-models.prebuilt-model-card.context')"
                :readonly="isRemoving"
                :error="errors.newContextName"
                :invalid="!!errors.newContextName"
                :maxlength="contextLimit"
                enforce-maxlength
                showErrorText="persistently"
            />
            <or-select
                v-else
                v-model="$v.importModal.$model.context"
                class="context-select"
                has-search
                :placeholder="$t('model.nlu-model.prebuilt-models.prebuilt-model-card.context')"
                :options="$props.contexts"
                :keys="{ label: 'name', value: 'id' }"
                :disabled="isRemoving || isAdded"
                :error="errors.context"
                :invalid="!!errors.context"
            />
             <or-alert
              v-show="showNoResponsesSelectedWarning"
              type="warning"
              :dismissible="false"
            >
              {{$t('validations.prebuilt-model-validation.noResponsesSelected')}}
            </or-alert>
            <Table
              class="table"
              :responses="responses"
              :entities="entities"
              :selectedItems="selectedItems"
            />
        </div>
    </div>
    <div
      slot="footer"
      class="footer"
    >
      <or-button
        v-if="isAdded"
        class="remove-btn"
        size="normal"
        type="secondary"
        color="red"
        :disabled="isLoadingList"
        @click.prevent="$emit('prebuilt-model::remove')"
      >
          {{ $t('model.nlu-model.prebuilt-models.prebuilt-model-card.removeDomain.title') }}
      </or-button>
      <or-button
          class="import-model--cancel-btn"
          size="normal"
          type="secondary"
          color="primary"
          :disabled="isRemoving || isLoadingList"
          @click="$refs.modal.close()"
      >
          {{ $t('generic.cancel') }}
      </or-button>
      <or-button
          size="normal"
          color="primary"
          class="import-btn"
          :loading="isImporting"
          :disabled="isRemoving || isImporting || isLoadingList"
          @click.prevent="importPrebuiltModel"
      >
          {{ isAdded ? $t('generic.save') : $t('generic.import') }}
      </or-button>
    </div>
</or-modal>
</template>

<script>
import uuid from 'uuid'
import { table_limits as tableLimits } from '@/modules/limits'
import { mapActions } from 'vuex'
import { prebuiltModelValidation } from '@/mixins/prebuilt-model-validation.mixin'
import Table from '../table'

export default {
  mixins: [prebuiltModelValidation],

  components: {
    Table
  },

  props: {
    importModelTitle: String,
    contexts: Array,
    prebuiltModel: Object,
    isRemoving: {
      type: Boolean,
      default: false
    },
    isAdded: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    responses () {
      return this.$props.prebuiltModel.responses.map(item => {
        return { name: item, id: uuid.v4() }
      })
    },
    entities () {
      return this.$props.prebuiltModel.entities.map(item => {
        return { ...item, id: uuid.v4() }
      })
    },
    contextLimit () {
      return tableLimits.context.max
    }
  },

  data () {
    return {
      importModal: {
        createNewContext: false,
        newContextName: '',
        context: ''
      },
      isLoadingList: false,
      selectedItems: {
        responses: [], // ids
        entities: [] // ids
      },
      isImporting: false,
      showNoResponsesSelectedWarning: false
    }
  },

  methods: {
    ...mapActions('knowledgebase/prebuiltModels', ['addPrebuiltModelToKM', 'getPrebuiltDetails']),

    handleImportModelClose () {
      this.importModal = {
        createNewContext: false,
        newContextName: '',
        context: ''
      }
    },

    async onOpen () {
      this.$v.importModal.$reset()

      if (this.$props.isAdded) {
        this.isLoadingList = true

        const response = await this.getPrebuiltDetails({ prebuiltModelId: this.$props.prebuiltModel.id })
        const addedResponsesNames = response.responses.map(i => i.name)
        const addedEntitiesNames = response.entities.map(e => e.name)
        this.$v.importModal.$model.context = this.$props.contexts.find(c => c.id === response.application)

        const responses = this.responses.filter(r => addedResponsesNames.includes(r.name)).map(r => r.id)
        const entities = this.entities.filter(e => addedEntitiesNames.includes(e.name)).map(e => e.id)
        this.selectedItems = { responses, entities }

        this.isLoadingList = false
      } else {
        this.selectedItems = {
          responses: this.responses.map(r => r.id),
          entities: this.entities.map(e => e.id)
        }
      }
    },

    async importPrebuiltModel () {
      this.$v.$touch()
      this.showNoResponsesSelectedWarning = false
      if (this.$v.importModal.$invalid) {
        console.debug('importPrebuiltModel failed:', this.$v, this.errors)
        return
      }
      if (this.selectedItems.responses.length === 0) {
        this.showNoResponsesSelectedWarning = true
        return
      }
      this.isImporting = true
      try {
        const context = this.importModal.createNewContext
          ? {
              name: this.importModal.newContextName,
              id: ''
            }
          : this.$props.contexts.find(ctx => ctx.id === this.importModal.context) ||
            this.$v.importModal.$model.context

        const responsesNames = this.responses
          .filter(r => {
            return this.selectedItems.responses.includes(r.id)
          })
          .map(r => r.name)

        const entitiesNames = this.entities
          .filter(e => {
            return this.selectedItems.entities.includes(e.id)
          })
          .map(e => e.name)

        await this.addPrebuiltModelToKM({
          prebuiltModelId: this.$props.prebuiltModel.id,
          appId: context.id,
          appName: context.name,
          shouldCreateNewApplication: this.importModal.createNewContext,
          responses: responsesNames,
          entities: entitiesNames
        })
        this.$refs.modal.close()
      } catch (error) {
        console.error('importPrebuiltModel:', error)
      }
      this.isImporting = false
    }
  },
  watch: {
    'selectedItems.responses': {
      handler (value) {
        if (value.length > 0) {
          this.showNoResponsesSelectedWarning = false
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.context-select.is-disabled  {
  ::v-deep .ui-select__content .ui-select__label .ui-select__display {
    color: $or-grey-dark;
  }

}

.prebuilt-model-import--modal.ui-modal {
    ::v-deep .ui-modal__container {
        .ui-modal__body {
            min-height: 350px;
            padding-bottom: 0;
        }

        .ui-modal__footer {
          box-shadow: 0 0 8px 1px rgba(0,0,0,0.08);
        }
    }
}
.prebuilt-model-import--modal {
    ::v-deep .ui-modal__container {
        .ui-modal__body {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            padding-bottom: $sp-4;
        }
    }

    .prebuilt-model-import--body {
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      justify-content: center;

      .spiner {
        display: flex;
        align-self: center;
      }
    }
    .import-model--context-type {
        padding: $sp-3 0;
    }
    .import-model--help-text {
        padding: $sp-3;
        background-color: rgba(64, 159, 236, 0.1);
    }
    .footer {
      display: flex;
      flex-grow: 1;
      justify-content: flex-end;

      .remove-btn {
        margin-right: auto;
      }

      .import-btn {
        margin-left: 16px;
      }
    }
    .import-model--cancel-btn {
        margin-right: 12spx;
    }
}
</style>
